import { z } from 'zod';

/**
 * The credential rules, in one place.
 *
 * These mirror the express-validator chains on POST /auth/signup,
 * POST /auth/register-invited, POST /auth/citizen-signup and
 * POST /auth/reset-password (backend/src/routes/auth.js). They were written
 * out separately in each page's own zod schema, so a change on either side —
 * a longer password minimum, a wider username charset — could silently pass
 * the client and fail at the server, which the invitation page then swallowed
 * because it had no onError.
 *
 * If you change a rule here, change the matching validator on the route.
 */

/** lowercase letters, numbers and underscore; min 3 (backend: matches(/^[a-z0-9_]+$/)) */
export const usernameField = z
  .string()
  .min(3, 'Username must be at least 3 characters')
  .regex(/^[a-z0-9_]+$/, {
    message: 'Username can only contain lowercase letters, numbers, and underscores',
  });

/** The hint rendered beside the Username label, so it says the same thing everywhere. */
export const USERNAME_HINT = '(lowercase letters, numbers, underscore)';

/**
 * Strips whatever the user typed down to the allowed charset as they type,
 * rather than rejecting it afterwards. Pass as react-hook-form's `setValueAs`.
 */
export const normalizeUsername = (value: unknown): unknown =>
  typeof value === 'string' ? value.toLowerCase().trim().replace(/[^a-z0-9_]/g, '') : value;

/**
 * The password minimum, in ONE place — the number and the copy together.
 *
 * Exporting only the zod rule was not enough: the hint beside the field is
 * prose, so three screens hardcoded "at least 6 characters" in JSX and kept
 * saying 6 after the schema moved. Render PASSWORD_HINT, never your own
 * sentence — same reason USERNAME_HINT exists above.
 */
export const PASSWORD_MIN = 8;

/** The hint rendered beside a password field, so it says the same thing everywhere. */
export const PASSWORD_HINT = `At least ${PASSWORD_MIN} characters.`;

export const passwordField = z
  .string()
  .min(PASSWORD_MIN, `Password must be at least ${PASSWORD_MIN} characters`);

export const newPasswordField = z
  .string()
  .min(PASSWORD_MIN, `New password must be at least ${PASSWORD_MIN} characters`);

/*
  A confirmation is not a second length rule. Restating the minimum here meant
  raising it took two edits and showed the user two errors for one mistake —
  the length belongs on the field being confirmed, once.
*/
export const confirmPasswordField = z.string().min(1, 'Please confirm your password');
export const firstNameField = z.string().min(1, 'First name is required');
export const lastNameField = z.string().min(1, 'Last name is required');

/** The five fields every account-creation surface collects. */
export const credentialFields = {
  firstName: firstNameField,
  lastName: lastNameField,
  username: usernameField,
  password: passwordField,
  confirmPassword: confirmPasswordField,
};

/** The password/confirmation check, applied identically wherever it is needed. */
export const passwordsMatch = {
  check: (data: { password: string; confirmPassword: string }) =>
    data.password === data.confirmPassword,
  options: { message: "Passwords don't match", path: ['confirmPassword'] as ['confirmPassword'] },
};

/**
 * The three fields every password-CHANGE surface collects.
 *
 * credentialFields above models "create an account" and nothing modelled
 * "change a password", so all four change/reset screens grew their own copy of
 * the rule and drifted apart — the citizen profile reached 8 on its own while
 * Pro stayed on 6. Compose from these instead. A reset flow that has no
 * current password (POST /auth/reset-password) takes newPasswordField and
 * confirmPasswordField directly.
 */
export const passwordChangeFields = {
  currentPassword: z.string().min(1, 'Enter your current password'),
  newPassword: newPasswordField,
  confirmPassword: confirmPasswordField,
};

/** passwordsMatch for the change/reset bundle, whose field is newPassword. */
export const newPasswordsMatch = {
  check: (data: { newPassword: string; confirmPassword: string }) =>
    data.newPassword === data.confirmPassword,
  options: { message: "Passwords don't match", path: ['confirmPassword'] as ['confirmPassword'] },
};
